import React, { useEffect, useMemo, useState } from "react";
import { Button, Flex, LoadingIndicator, TextInput } from "@jsq/ds/core";
import { Sizes } from "@jsq/ds/styles";
import { type Sandbox } from "../consts";
import { SandboxListbox } from "../SandboxListbox";
import * as styles from "./styles_vanilla.css";

const ACTIVE_LABEL_ID = "remocal-picker-active-label";
const INACTIVE_LABEL_ID = "remocal-picker-inactive-label";

export type SandboxPickerProps = {
  /** True while this pane is the visible view — used to reset transient state. */
  active: boolean;
  onBack: () => void;
  onClose: () => void;
  onSwitch: (routingKey: string) => void;
  sandboxes: ReadonlyArray<Sandbox> | undefined;
  sandboxesLoading: boolean;
  currentRoutingKey: string | undefined;
  closeButtonRef?: React.RefObject<HTMLButtonElement>;
};

export const SandboxPicker: React.FC<SandboxPickerProps> = ({
  active,
  onBack,
  onClose,
  onSwitch,
  sandboxes,
  sandboxesLoading,
  currentRoutingKey,
  closeButtonRef,
}) => {
  const [searchQuery, setSearchQuery] = useState("");

  // Reset the search each time the picker slides into view.
  useEffect(() => {
    if (active) setSearchQuery("");
  }, [active]);

  const groups = useMemo(() => {
    const query = searchQuery.toLowerCase();
    const visible = (sandboxes ?? []).filter(
      (sb) =>
        sb.routingKey !== currentRoutingKey &&
        (sb.name.toLowerCase().includes(query) || sb.routingKey.toLowerCase().includes(query)),
    );
    const activeSandboxes = visible.filter((sb) => sb.ready ?? true);
    const inactiveSandboxes = visible.filter((sb) => !(sb.ready ?? true));

    return [
      {
        labelId: ACTIVE_LABEL_ID,
        label: "Active sandboxes",
        sandboxes: activeSandboxes,
      },
      {
        labelId: INACTIVE_LABEL_ID,
        label: "Inactive sandboxes",
        sandboxes: inactiveSandboxes,
        disabled: true,
      },
    ];
  }, [sandboxes, currentRoutingKey, searchQuery]);

  const hasResults = groups.some((group) => group.sandboxes.length > 0);

  return (
    <Flex flexDirection="column" gap={Sizes.MD} className={styles.bodyStyle}>
      <Flex justifyContent="space-between" alignItems="center">
        <Button variant="muted" icon="arrow-left" onClick={onBack}>
          Back
        </Button>
        <Button
          ref={active ? closeButtonRef : undefined}
          childAsIcon={{ type: "xmark" }}
          variant="muted"
          aria-label="Close"
          onClick={onClose}
        />
      </Flex>
      <TextInput
        icon="search"
        placeholder="Search sandboxes..."
        value={searchQuery}
        onSetValue={setSearchQuery}
        isFullWidth
        aria-label="Search sandboxes"
      />
      {sandboxesLoading ? (
        <Flex justifyContent="center" alignItems="center" style={{ padding: Sizes.MD }}>
          <LoadingIndicator size="small" />
        </Flex>
      ) : !hasResults ? (
        <div className={styles.emptyStyle}>No sandboxes found.</div>
      ) : (
        <SandboxListbox groups={groups} ariaLabel="Sandboxes" bordered onSwitch={onSwitch} />
      )}
    </Flex>
  );
};
